/**
 * Secure Access → Posture Profiles
 *
 * CRUD for named Device Posture Profiles (V31). A profile is a reusable set of
 * posture requirements; a policy picks one by name instead of authoring
 * predicates inline. Editing a profile changes every policy that uses it on
 * the next Compile & Sign; deleting is refused while any policy references it.
 */

import { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCw, Trash2, Pencil, ShieldCheck } from 'lucide-react';
import DataTable, { type Column } from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import PostureProfileModal from '../../components/access/PostureProfileModal';
import PostureCatalogPanel from '../../components/access/PostureCatalogPanel';
import ZtnaChip from '../../components/access/ZtnaChip';
import { useToast } from '../../components/common/ToastContainer';
import accessService, {
  type PostureProfile,
  type PostureProfileData,
} from '../../services/access.service';
import { fmtRequirement, platformList, profileWarnings } from '../../data/postureCatalog';

const AccessPostureProfiles = () => {
  const toast = useToast();

  const [profiles, setProfiles] = useState<PostureProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PostureProfile | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PostureProfile | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchProfiles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setProfiles((await accessService.listPostureProfiles()) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load posture profiles');
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchProfiles();
  }, [fetchProfiles]);

  const handleSave = async (data: PostureProfileData) => {
    try {
      if (editTarget) {
        await accessService.updatePostureProfile(editTarget.id, data);
        toast.success(
          'Profile updated',
          editTarget.ruleCount > 0
            ? `${data.name} — compile policies to push the change to the fleet`
            : data.name,
        );
        setEditTarget(null);
      } else {
        await accessService.createPostureProfile(data);
        toast.success('Profile created', `${data.name} — pick it in a policy's Device section`);
        setIsAddOpen(false);
      }
      await fetchProfiles();
    } catch (err) {
      toast.error('Save failed', err instanceof Error ? err.message : 'Could not save the profile');
      throw err; // keeps the modal open
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await accessService.deletePostureProfile(deleteTarget.id);
      toast.success('Profile deleted', deleteTarget.name);
      setDeleteTarget(null);
      await fetchProfiles();
    } catch (err) {
      toast.error('Delete failed', err instanceof Error ? err.message : 'Could not delete the profile');
    } finally {
      setDeleting(false);
    }
  };

  const columns: Column<PostureProfile>[] = [
    {
      key: 'name',
      label: 'Profile',
      sortable: true,
      render: (p) => (
        <div>
          <div className="font-medium text-gray-900">{p.name}</div>
          {p.description && <div className="text-xs text-gray-500">{p.description}</div>}
        </div>
      ),
    },
    {
      key: 'requirements',
      label: 'Requirements',
      render: (p) => {
        const entries = Object.entries(p.requirements ?? {});
        if (entries.length === 0) return <span className="text-gray-400 text-sm">none</span>;
        const { neverPass } = profileWarnings(p.requirements);
        return (
          <div>
            <div className="flex flex-wrap gap-1">
              {entries.map(([k, v]) => (
                <span
                  key={k}
                  className="inline-flex items-center rounded bg-teal-50 border border-teal-200 px-1.5 py-0.5 text-[11px] text-teal-700"
                >
                  {fmtRequirement(k, v)}
                </span>
              ))}
            </div>
            {neverPass.length > 0 && (
              <div className="mt-1 text-[11px] text-amber-700" data-testid="profile-never-pass">
                ⚠ {platformList(neverPass)} devices never pass
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'ruleCount',
      label: 'Used by',
      sortable: true,
      render: (p) =>
        p.ruleCount === 0 ? (
          <span className="text-gray-400 text-sm">no policies</span>
        ) : (
          <span className="text-sm text-gray-700" title={p.ruleNames.join('\n')}>
            {p.ruleCount} polic{p.ruleCount === 1 ? 'y' : 'ies'}
          </span>
        ),
    },
    {
      key: 'actions',
      label: '',
      render: (p) => (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            className="p-1.5 rounded text-gray-400 hover:bg-gray-100 hover:text-primary-600"
            title="Edit profile"
            onClick={() => setEditTarget(p)}
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            type="button"
            className={`p-1.5 rounded text-gray-400 ${
              p.ruleCount > 0 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-100 hover:text-danger-600'
            }`}
            title={p.ruleCount > 0 ? 'In use by a policy — change those policies first' : 'Delete profile'}
            disabled={p.ruleCount > 0}
            onClick={() => setDeleteTarget(p)}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-gray-900">Posture Profiles</h1>
            <ZtnaChip />
          </div>
          <p className="text-gray-600 mt-1">
            Named sets of device requirements. A policy picks a profile by name; edits reach every policy that
            uses it on the next Compile &amp; Sign.
          </p>
        </div>
        <div className="flex space-x-2">
          <button type="button" className="btn-secondary" onClick={() => void fetchProfiles()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 inline ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button type="button" className="btn-primary" onClick={() => setIsAddOpen(true)}>
            <Plus className="w-4 h-4 mr-2 inline" />
            Add Profile
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {error}
        </div>
      )}

      <div className="card">
        <DataTable
          columns={columns}
          data={profiles}
          loading={loading}
          searchPlaceholder="Search profiles…"
          emptyMessage="No posture profiles yet — policies apply to any device until you add one"
        />
      </div>

      <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-600 flex gap-2">
        <ShieldCheck className="w-4 h-4 text-teal-600 shrink-0" />
        <p>
          Requirements are matched against each device's verified posture: the client's signed self-report, checked
          by the gateway against the device's key. A fact the device does not report, or reports as unknown, never
          satisfies a requirement on pass — so check what each platform reports before you require it.
        </p>
      </div>

      <PostureCatalogPanel />

      {(isAddOpen || editTarget) && (
        <PostureProfileModal
          key={editTarget?.id ?? 'new'}
          isOpen
          profile={editTarget}
          onClose={() => (editTarget ? setEditTarget(null) : setIsAddOpen(false))}
          onSave={handleSave}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete posture profile"
        message={`Delete '${deleteTarget?.name}'? Policies cannot reference it afterwards.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default AccessPostureProfiles;
